/**
 * Candidate api-client seam.
 *
 * One typed interface, two implementations:
 *   - `createMockClient()` — in-memory fixtures that drive the auth flow and seed the whole ledger
 *     (all seven card states), so every screen is buildable and demoable before / without the
 *     backend.
 *   - `createHttpClient()` — real fetch against `VITE_API_BASE_URL` via the generated
 *     `candidateClient`, with the candidate JWT as a Bearer header and the ~15-min access token
 *     rotated transparently against the refresh token.
 *
 * Selection is env-driven (`shouldUseMocks()`), so screens import `apiClient` and never know which
 * backing they got.
 *
 * ## What is real and what is mocked
 * The auth + ledger methods hit EXISTING Phase-1 endpoints (`/api/auth/*`, `GET /api/requests`) with
 * the real `@bymyword/api-types` request-body types. `joinWaitlist` / `requestAccountDeletion` /
 * `getReferral` / `claimReferral` hit the WP-B18 endpoints (`POST /api/waitlist`, `DELETE /api/account`,
 * `GET /api/referrals/me`, `POST /api/referrals/claim`) — all now live. Referral response bodies stay
 * under the Phase-1 carve-out (untyped in OpenAPI → hand-authored in `wire.ts`); the claim REQUEST
 * body uses the generated `ClaimReferralBody`.
 */
import type { TokenResponse } from './wire';
import type { LedgerCard } from './ledger';
import type { ReferenceContent } from './letter-content';
import { createMockClient } from './api-client.mock';
import { createHttpClient } from './api-client.http';

/** WP-B18 forward-seam: the persona-picker waitlist capture (employer/recruiter "coming soon"). */
export interface WaitlistInput {
  email: string;
  persona: 'employer' | 'recruiter';
}

/**
 * The candidate's referral surface (`GET /api/referrals/me`, WP-B18). `shareUrl` is built server-side
 * from a config base + the code (`{base}/{code}`) — display it verbatim, never reconstruct a domain.
 * `bonusEarned` is whether the caller's referral bonus has already landed (someone they invited
 * verified their email and sent their first request) — it surfaces as an "earned" status, not a claim.
 */
export interface ReferralInfo {
  code: string;
  shareUrl: string;
  bonusEarned: boolean;
}

/**
 * The new-request wizard's payload (WP-F8b). Mirrors the backend `CreateRequestBody` minus the
 * candidate id, which the server reads from the token. `refereeEmail` is required — it is the
 * system-sent invite channel and the backend rejects a request without it (DESIGN-NOTES delta a:
 * the web has no share sheet, so email is mandatory to send).
 *
 * `refereePermissionConfirmed` is required and non-optional on purpose, mirroring the wire: the
 * server refuses an absent field exactly as it refuses an explicit `false`, so a caller that has
 * not thought about the attestation should fail to compile rather than fail at runtime.
 */
export interface NewRequestInput {
  refereeName: string;
  refereeEmail: string;
  relationshipContext?: string | null;
  companyName?: string | null;
  personalNote?: string | null;
  /**
   * The CANDIDATE's assertion that they have already asked this person and the person agreed to be
   * contacted. It is not the referee's consent — the referee has not met the product yet, and their
   * own consent is taken later, in the referee flow. `false` (or absent) is refused server-side with
   * `referee_permission_required`, a code kept distinct from `invalid_request` so the wizard can
   * point at the confirmation control rather than the email field.
   */
  refereePermissionConfirmed: boolean;
}

/** The created request's id + wire status + the non-blocking EU-referee compliance flag. */
export interface CreateRequestResult {
  requestId: string;
  status: string;
  euRefereeFlagged: boolean;
}

/**
 * A reference's unlock state + the free one-tap methods available right now (WP-F11a —
 * `GET /api/references/{id}/unlock`, `UnlockOptionsResponse`). `alreadyUnlocked` is the entitlement
 * source of truth (the Phase-1 ledger wire still cannot carry it — DESIGN-NOTES). Both `*Available`
 * flags are false once unlocked.
 */
export interface UnlockOptions {
  alreadyUnlocked: boolean;
  freeCreditAvailable: boolean;
  referralBonusAvailable: boolean;
}

/**
 * A minted Stripe hosted-Checkout session (WP-F11a — `POST /api/references/{id}/checkout`,
 * `StartCheckoutResponse`). Redirect the browser to `redirectUrl`; the configured success/cancel
 * URLs bring it back, and the webhook grants the unlock server-side.
 */
export interface CheckoutSession {
  sessionId: string;
  redirectUrl: string;
}

/**
 * The PDF tier's answer (WP-F11a-follow-up — `GET …/content/pdf`, `ReferencePdfResponse`).
 * `ready` → `downloadUrl` is a short-TTL presigned R2 GET to open; `pending` → the post-sign render
 * is still in flight and `downloadUrl` is null (the viewer offers "try again", never a dead error).
 */
export interface PdfDownload {
  status: 'ready' | 'pending';
  downloadUrl: string | null;
}

/** One entry of the request-detail status timeline (view shape mirrors the wire entry). */
export interface RequestTimelineEntry {
  fromStatus: string | null;
  toStatus: string;
  actor: string;
  occurredAt: string;
}

/** A request's detail: the ledger card, the candidate's own note, and the status timeline. */
export interface RequestDetail {
  card: LedgerCard;
  personalNote: string | null;
  timeline: RequestTimelineEntry[];
}

export interface CandidateApiClient {
  // --- auth (Phase-1: /api/auth/*) ---
  /**
   * Request an email sign-in code. NEUTRAL by contract — it never reveals whether an account
   * exists, so this resolves `void` regardless. The captcha token is attached internally.
   */
  requestOtp(email: string): Promise<void>;
  /** Verify a code for a token pair. First sign-in creates the account (userId present on success). */
  verifyOtp(email: string, code: string): Promise<TokenResponse>;

  // --- ledger (Phase-1: GET /api/requests) ---
  /** The candidate's reference ledger, newest-first, already mapped to card view-models. */
  getLedger(): Promise<LedgerCard[]>;

  // --- requests (Phase-1: /api/requests/*) — WP-F8b ---
  /**
   * Create a reference request (the wizard). The server enforces the referee-permission attestation
   * / self-referee / duplicate / suppression / velocity; failures surface as a
   * {@link CandidateApiError} whose `code` names the outcome (`referee_permission_required`,
   * `self_referee`, `duplicate_request`, `referee_suppressed`, `rate_limited`, `invalid_request`).
   */
  createRequest(input: NewRequestInput): Promise<CreateRequestResult>;
  /** Read a request's detail + status timeline. `not_found` when it is not the candidate's. */
  getRequestDetail(id: string): Promise<RequestDetail>;
  /**
   * Resend the referee invite. Gated by the 48h cooldown server-side — a too-soon call is
   * `rate_limited` (`resend_too_soon`); a request past a resendable state is `conflict`
   * (`not_resendable`).
   */
  resendRequest(id: string): Promise<void>;
  /** Withdraw a request. `conflict` (`cannot_cancel`) once the referee has engaged. */
  cancelRequest(id: string): Promise<void>;

  // --- unlock / monetization (WP-F11a — real dual-path unlock: free/referral one-tap + Stripe) ---
  /**
   * The reference's unlock state + available one-tap methods (`GET /api/references/{id}/unlock`).
   * `not_found` when it is not the candidate's (indistinguishable from not existing).
   */
  getUnlockOptions(id: string): Promise<UnlockOptions>;
  /**
   * Consume the base free-unlock credit to unlock the reference (`POST …/unlock/free`). `conflict`
   * (`already_unlocked`) if a rail already unlocked it — treat as success; `server` (422
   * `no_free_credit`) when none remains; `not_found` if it is not the candidate's.
   */
  unlockWithFreeCredit(id: string): Promise<void>;
  /**
   * Consume an earned referral bonus to unlock the reference (`POST …/unlock/referral`). Same failure
   * shape as {@link unlockWithFreeCredit} but with `no_referral_bonus` for the 422.
   */
  unlockWithReferralBonus(id: string): Promise<void>;
  /**
   * Mint a Stripe hosted-Checkout session for a paid unlock (`POST …/checkout`). The caller redirects
   * to `redirectUrl`. `conflict` (`already_unlocked`) if a rail already unlocked it (no session, no
   * charge — treat as success); `not_found` if it is not the candidate's. NEVER surfaced on IAP.
   */
  startUnlockCheckout(id: string): Promise<CheckoutSession>;

  // --- letter / unlock content (WP-B19 content chokepoint — key points free, letter gated) ---
  /**
   * The reference's content view-model for the unlock sheet + letter viewer: the free key-point
   * teaser (always), the entitlement (from the unlock-options endpoint), and the gated letter body
   * when unlocked. Takes the ledger `card` (the referee's identity + signed date come from it;
   * the words + entitlement come from the content/unlock endpoints).
   */
  getReference(card: LedgerCard): Promise<ReferenceContent>;
  /**
   * Get a short-TTL presigned download for the reference's server-generated PDF
   * (`GET …/content/pdf`). `ready` carries the `downloadUrl` to open; `pending` means the post-sign
   * render is still in flight (never a 404 — the viewer offers "try again"). `forbidden` when locked.
   */
  getLetterPdf(id: string): Promise<PdfDownload>;

  // --- WP-B18 endpoints ---
  /** Capture an employer/recruiter waitlist signup from the persona picker (real: POST /api/waitlist). */
  joinWaitlist(input: WaitlistInput): Promise<void>;
  /** Fetch the candidate's referral code + share link (real: GET /api/referrals/me, minted lazily). */
  getReferral(): Promise<ReferralInfo>;
  /**
   * Claim a friend's referral code (real: POST /api/referrals/claim). Attribution is
   * claim-before-first-use and immutable once set. Failures surface as a {@link CandidateApiError}
   * whose `code` names the outcome: `invalid_code` (malformed / unknown), `self_referral` (the
   * caller's own code), `already_referred` (a code is already attached), or `not_eligible` (the
   * caller has already sent a request, so it is too late to attribute the signup).
   */
  claimReferral(code: string): Promise<void>;
  /** Delete (anonymise) the account server-side (real: DELETE /api/account). Idempotent. */
  requestAccountDeletion(): Promise<void>;
}

/**
 * Which backing to use.
 *   unset  → mocks in dev, HTTP in prod
 *   "true" → force mocks · "false" → force HTTP
 */
export function shouldUseMocks(): boolean {
  const flag = import.meta.env.VITE_USE_MOCKS;
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  return import.meta.env.DEV;
}

export const apiClient: CandidateApiClient = shouldUseMocks() ? createMockClient() : createHttpClient();

export { createMockClient, createHttpClient };
export { CandidateApiError } from './errors';
